// WHATSAPP-OTP-STANDARD-001 (Owner 04.10.2026) — Zustellkanal der Bestätigungscodes im Admin-Portal.
//
// Vertrag (backend/src/lib/otpChannel.js + routes/auth.js, derselbe Zweig):
//   • Nur Code-AUSLÖSENDE POSTs tragen `X-Voogo-Capabilities: otp_whatsapp_v1`. Alle anderen Anfragen
//     bleiben byte-gleich. Die Liste unten ist abschließend (Pfad relativ zur API-Basis + Methode POST).
//   • Antwortet ein Backend, das das Token nicht kennt, mit 400 `customer_phone_auth_capability_required`,
//     wird die Anfrage GENAU EINMAL ohne Token wiederholt und das Token bis zum Neuladen nicht mehr gesendet.
//   • `channel` ('whatsapp' | 'sms') wird nur gelesen, wenn die beantwortete Anfrage das Token trug.
//     Fehlt es oder ist es unbekannt, bleibt der bisherige Text (= SMS wie heute).
//   • „Lieber per SMS" = dieselbe Anfrage mit `channel: 'sms'` im Körper.
// Vorlage: iOS VoogoKit OTPDeliveryChannel.swift (gleiche Regeln, gleiche Texte).
import { useCallback, useRef, useState } from 'react';

export const OTP_CAPABILITY_HEADER = 'X-Voogo-Capabilities';
export const OTP_CAPABILITY = 'otp_whatsapp_v1';
export const OTP_LEGACY_REJECTION = 'customer_phone_auth_capability_required';
export const OTP_CHANNELS = Object.freeze(['sms', 'whatsapp']);

// Admin-Portal: genau diese Code-Anfragen. Der Login ist bewusst NICHT dabei — Admin-Konten haben
// keine SMS-Zwei-Faktor-Anmeldung (backend smsTwoFactorStatus: eligibleRole = role !== 'admin').
export const OTP_CARRIER_PATHS = Object.freeze([
  '/auth/forgot/request',
  '/auth/me/phone/request',
]);

export const OTP_TEXT = Object.freeze({
  sentViaWhatsApp: 'Wir haben dir den Code per WhatsApp geschickt.',
  sentViaSms: 'Wir haben dir den Code per SMS geschickt.',
  preferSms: 'Lieber per SMS',
  preferSmsHint: 'Schickt denselben Code per SMS statt per WhatsApp.',
});

let advertising = true;   // pro Seitensitzung; ein altes Backend schaltet es bis zum Neuladen ab

export const otpCapabilityActive = () => advertising;
export const stopOtpCapability = () => { advertising = false; };
export const resetOtpCapabilityForTests = () => { advertising = true; };

// Nur relative API-Pfade zählen; absolute oder protokollrelative Adressen bekommen nie ein Token.
export function otpPathOf(url) {
  if (typeof url !== 'string' || !url || /^[a-z][a-z0-9+.-]*:/i.test(url) || url.startsWith('//')) return null;
  const bare = url.replace(/[?#].*$/, '').replace(/\/+$/, '');
  return bare.startsWith('/') ? bare : `/${bare}`;
}

export function isOtpCarrier(method, url) {
  if (String(method || 'get').toUpperCase() !== 'POST') return false;
  return OTP_CARRIER_PATHS.includes(otpPathOf(url));
}

const tokensOf = (raw) => String(raw || '').split(',').map((value) => value.trim()).filter(Boolean);
export const headerCarriesOtp = (raw) => tokensOf(raw).includes(OTP_CAPABILITY);

export const isOtpLegacyRejection = (status, data) => status === 400 && data?.error === OTP_LEGACY_REJECTION;

// Kanal nur aus einer Antwort, deren Anfrage das Token trug; sonst null (= bisheriger Text).
export function otpChannelOf(data, advertised) {
  if (!advertised) return null;
  const channel = data?.channel;
  return OTP_CHANNELS.includes(channel) ? channel : null;
}

export const otpChannelLabel = (channel) => (channel === 'whatsapp' ? 'WhatsApp' : 'SMS');
export function otpSentText(channel, fallback = null) {
  if (channel === 'whatsapp') return OTP_TEXT.sentViaWhatsApp;
  if (channel === 'sms') return OTP_TEXT.sentViaSms;
  return fallback;
}

// ── Axios ───────────────────────────────────────────────────────────────────────────────────────
const readHeader = (headers) => {
  if (!headers) return undefined;
  if (typeof headers.get === 'function') return headers.get(OTP_CAPABILITY_HEADER);
  const key = Object.keys(headers).find((name) => name.toLowerCase() === OTP_CAPABILITY_HEADER.toLowerCase());
  return key ? headers[key] : undefined;
};
const writeHeader = (headers, value) => {
  if (typeof headers.set === 'function') {
    if (value) headers.set(OTP_CAPABILITY_HEADER, value);
    else headers.delete(OTP_CAPABILITY_HEADER);
    return;
  }
  Object.keys(headers)
    .filter((name) => name.toLowerCase() === OTP_CAPABILITY_HEADER.toLowerCase())
    .forEach((name) => { delete headers[name]; });
  if (value) headers[OTP_CAPABILITY_HEADER] = value;
};

export const responseAdvertisedOtp = (response) => headerCarriesOtp(readHeader(response?.config?.headers));
// Fehlerantworten (z. B. 401 sms_2fa_required) tragen die Anfrage in `error.config`.
export const errorAdvertisedOtp = (error) => headerCarriesOtp(readHeader(error?.config?.headers));

export function installOtpCapability(instance) {
  instance.interceptors.request.use((config) => {
    if (!advertising || !isOtpCarrier(config.method, config.url)) return config;
    config.headers = config.headers || {};
    const tokens = tokensOf(readHeader(config.headers));   // vorhandene Tokens bleiben erhalten
    if (!tokens.includes(OTP_CAPABILITY)) tokens.push(OTP_CAPABILITY);
    writeHeader(config.headers, tokens.join(', '));
    return config;
  });
  instance.interceptors.response.use(undefined, (error) => {
    const config = error?.config;
    if (!config || config.otpCapabilityRetried || !headerCarriesOtp(readHeader(config.headers))
        || !isOtpLegacyRejection(error.response?.status, error.response?.data)) {
      return Promise.reject(error);
    }
    stopOtpCapability();
    config.otpCapabilityRetried = true;
    writeHeader(config.headers, tokensOf(readHeader(config.headers)).filter((t) => t !== OTP_CAPABILITY).join(', '));
    return instance.request(config);
  });
  return instance;
}

// ── fetch (Passwort vergessen umgeht den 401-Interceptor bewusst) ─────────────────────────────────
// Rückgabe: { response, data, advertised } — `advertised` sagt, ob die BEANTWORTETE Anfrage das Token trug.
export async function postJsonWithOtpCapability({ fetchImpl, base, path, body, headers = {} }) {
  const send = async (advertise) => {
    const requestHeaders = { ...headers };
    if (advertise) requestHeaders[OTP_CAPABILITY_HEADER] = OTP_CAPABILITY;
    const response = await fetchImpl(`${base}${path}`, {
      method: 'POST',
      headers: requestHeaders,
      credentials: 'include',
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    return { response, data, advertised: advertise };
  };
  const advertise = advertising && isOtpCarrier('POST', path);
  const first = await send(advertise);
  if (!advertise || !isOtpLegacyRejection(first.response?.status, first.data)) return first;
  stopOtpCapability();
  return send(false);
}

// ── Anzeige-Zustand eines Code-Formulars ───────────────────────────────────────────────────────
// Der Knopf „Lieber per SMS" erscheint nur, wenn der Code nachweislich per WhatsApp ging; die Refs
// sind der Doppelklick-Schutz (zwei Klicks im selben Tick sehen sonst beide den alten Zustand).
export function useOtpDelivery() {
  const [channel, setChannel] = useState(null);
  const [switching, setSwitching] = useState(false);
  const channelRef = useRef(null);
  const switchingRef = useRef(false);

  const receive = useCallback((next) => {
    const value = OTP_CHANNELS.includes(next) ? next : null;
    channelRef.current = value;
    switchingRef.current = false;
    setChannel(value);
    setSwitching(false);
  }, []);
  const beginSmsSwitch = useCallback(() => {
    if (channelRef.current !== 'whatsapp' || switchingRef.current) return false;
    switchingRef.current = true;
    setSwitching(true);
    return true;
  }, []);
  const failSmsSwitch = useCallback(() => {
    switchingRef.current = false;
    setSwitching(false);
  }, []);
  const reset = useCallback(() => receive(null), [receive]);

  return { channel, switching, showsPreferSms: channel === 'whatsapp', receive, beginSmsSwitch, failSmsSwitch, reset };
}
